import React, {useEffect, useMemo, useRef, useState} from "react";
import {Mui, MuiIcons} from "@leap4/frontend";

const {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} = Mui;
const {
    ContentCopy: ContentCopyIcon,
    Check: CheckIcon,
    OpenInNew: OpenInNewIcon,
    Search: SearchIcon,
} = MuiIcons;

interface IPartnerOption {
    id: string;
    name: string;
}

interface ILoginUrlsTableProps {
    partners: IPartnerOption[];
    buildUrl: (partnerId: string) => string;
    disabled?: boolean;
    title?: string;
    maxHeight?: number;
    unsavedPartnerIds?: string[];
}

interface ILoginUrlRow extends IPartnerOption {
    url: string;
    unsaved: boolean;
}

export const LoginUrlsTable = ({partners, buildUrl, disabled = false, title, maxHeight = 360, unsavedPartnerIds}: ILoginUrlsTableProps) => {
    const [query, setQuery] = useState("");
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [unsavedDialogPartner, setUnsavedDialogPartner] = useState<string | null>(null);
    const copyTimer = useRef<ReturnType<typeof setTimeout>>();

    const rows = useMemo<ILoginUrlRow[]>(() => {
        return partners.map(p => ({
            ...p,
            url: buildUrl(p.id),
            unsaved: !!unsavedPartnerIds && unsavedPartnerIds.includes(p.id),
        }));
    }, [partners, buildUrl, unsavedPartnerIds]);

    const filteredRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => r.name.toLowerCase().includes(q) || r.url.toLowerCase().includes(q));
    }, [rows, query]);

    const handleCopy = async (id: string, url: string) => {
        if (disabled) return;
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            // clipboard unavailable; silent — non-critical
        }
        setCopiedId(id);
        clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(() => setCopiedId(null), 1500);
    };

    // The URL only starts working after the partner selection is persisted;
    // block copy/open for unsaved partners and tell the user to save first.
    const guardUnsaved = (row: ILoginUrlRow): boolean => {
        if (row.unsaved) {
            setUnsavedDialogPartner(row.name);
            return true;
        }
        return false;
    };

    const copyTooltip = (row: ILoginUrlRow): string => {
        if (disabled) return "Generate client first";
        if (row.unsaved) return "Not saved yet";
        return copiedId === row.id ? "Copied" : "Copy login URL";
    };

    const openTooltip = (row: ILoginUrlRow): string => {
        if (disabled) return "Generate client first";
        if (row.unsaved) return "Not saved yet";
        return "Open in new tab";
    };

    useEffect(() => () => clearTimeout(copyTimer.current), []);

    if (partners.length === 0) return null;

    return (
        <Box>
            {title && (
                <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                    {title}
                </Typography>
            )}
            <TextField
                size="small"
                fullWidth
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                placeholder="Filter logins"
                sx={{mb: 1}}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{fontSize: 18}} />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <TableContainer component={Paper} variant="outlined" sx={{maxHeight}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{"& th": {backgroundColor: "background.paper"}}}>
                            <TableCell>Partner</TableCell>
                            <TableCell>Login URL</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.map(row => (
                            <TableRow key={row.id} hover>
                                <TableCell sx={{fontWeight: 600, whiteSpace: "nowrap"}}>
                                    {row.name}
                                    {row.unsaved && <Chip label="Unsaved" size="small" color="warning" sx={{ml: 1}} />}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 0,
                                        width: "100%",
                                        fontFamily: "monospace",
                                        fontSize: 12.5,
                                        color: "text.secondary",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    <Tooltip title={row.url}>
                                        <span>{row.url}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right" sx={{whiteSpace: "nowrap"}}>
                                    <Tooltip title={copyTooltip(row)}>
                                        <Box component="span" sx={{display: "inline-flex"}}>
                                            <IconButton
                                                size="small"
                                                aria-label="Copy login URL"
                                                disabled={disabled}
                                                onClick={() => { if (!guardUnsaved(row)) handleCopy(row.id, row.url); }}
                                                color={copiedId === row.id ? "success" : "default"}
                                            >
                                                {copiedId === row.id ? (
                                                    <CheckIcon sx={{fontSize: 18}} />
                                                ) : (
                                                    <ContentCopyIcon sx={{fontSize: 18}} />
                                                )}
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={openTooltip(row)}>
                                        <Box component="span" sx={{display: "inline-flex"}}>
                                            <IconButton
                                                size="small"
                                                aria-label="Open in new tab"
                                                disabled={disabled}
                                                component="a"
                                                href={row.unsaved ? undefined : row.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e: React.MouseEvent) => { if (guardUnsaved(row)) e.preventDefault(); }}
                                            >
                                                <OpenInNewIcon sx={{fontSize: 18}} />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{color: "text.secondary"}}>
                                    No matching logins
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={unsavedDialogPartner !== null} onClose={() => setUnsavedDialogPartner(null)}>
                <DialogTitle>Organization not saved</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`The partner selection for "${unsavedDialogPartner ?? ""}" hasn't been saved yet. Save the organization first, then copy or open the login URL.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" autoFocus onClick={() => setUnsavedDialogPartner(null)}>
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
